import React from 'react'
import moment from 'moment'
import { ArrowDown, ArrowRight } from 'pcln-icons'
import { ActiveFlexDateCell } from './ActiveFlexDateCell'
import { FlexDateCell, Label, StyledText } from './FlexDatesRoundTrip.styles'
import { cellProps, selectedCellProps, CENTER_COL_INDEX, CENTER_ROW_INDEX, CENTER_INDEX } from './constants'

/**
 * --------------------------------------------------
 * ----------- Position Getter functions ------------
 * --------------------------------------------------
 * Based on grid index i,
 * Determine what "type" of cell returned
 *
 * i.e. Tile i===8 should be `Departure Label`
 * Hence isDepartLabel(8) will be true.
 *
 * @param {i} - tile index
 * i.e. [0][1][2]
 *      [3][4][5]
 *
 * ^arranged this way, but it's 9 x 9
 * @returns True/False
 * --------------------------------------------------
 */

// First Cell is Empty
const isFirstCell = (i) => i === 0

// First row except label is empty
const isFirstRow = (i) => i > 1 && i < 8

// First Column are date labels
const isFirstColumn = (i) => i > 8 && !(i % 8)

// Second Row 1st column (index 8) is "Depart" Label
const isDepartLabel = (i) => i === 8

// First return label is "Return" Label
const isReturnLabel = (i) => i === 1

// Second Row is i in range(8, 16)
const isSecondRow = (i) => i > 8 && i < 16

// Selected Cell is center, i===44
const isSelected = (i) => i === CENTER_INDEX

/**
 * --------------------------------------------------
 * ------------ Get Date Labels Function ------------
 *
 * Assume travelDate is like 2022-08-03
 * We want to convert it into "Wed, Aug 03"
 * for both horizontal AND vertical labels
 *
 * @param {i} - tile index, see above
 * @param {travelDate} - departure date
 * @param {isHorizontal} - if tile in 2nd horizontal row
 * @returns  "Wed, Aug 03"
 * --------------------------------------------------
 */
const getDateLabels = (i, travelDate, isHorizontal) => {
  const diff = isHorizontal ? i - CENTER_ROW_INDEX : (i - CENTER_COL_INDEX) / 8
  const newDate = moment(travelDate).add(diff, 'd').format('llll')
  const secondCommaIndex = newDate.indexOf(',', 4)
  return newDate.slice(0, secondCommaIndex)
}

/**
 * --------------------------------------------------
 * ---------- Generate Cell/Tile function -----------
 *
 * Applies position getter functions to generate
 * cells/tiles
 *
 *
 * @param {day} - Array of qualified flex dates
 * @param {i} - Tile index, see above
 * @param {priceThreshold} - Object: {low, middle, high}
 * @param {minimumRetailPrice} - Lowest retail price
 * @param {travelDate,returnDate} - from/to dates
 * @param {url} - window.location.href
 * @returns True/False
 * --------------------------------------------------
 */
export const generateCell = (day, i, priceThreshold, props) => {
  const { minimumRetailPrice, travelDate, returnDate, url } = props

  if (isFirstCell(i) || isFirstRow(i)) {
    return <FlexDateCell {...cellProps} key={i} />
  }

  // i = 16, 24, 32, 40, 48, 56, 64 (last one)
  if (isFirstColumn(i)) {
    return (
      <Label
        bg={'background.lightest'}
        m={0}
        mr={'8px'}
        color={'text.dark'}
        data-testid='first-column-label'
        key={i}
      >
        <StyledText fontSize={1} bold>
          {' '}
          {getDateLabels(i, travelDate, false)}
        </StyledText>
      </Label>
    )
  }

  // i = 9, 10, 11, 12, 13, 14, 15
  if (isSecondRow(i)) {
    return (
      <FlexDateCell {...cellProps} data-testid='second-row-label' isTopLabel key={i}>
        <StyledText fontSize={1} bold>
          {getDateLabels(i, returnDate, true)}
        </StyledText>
      </FlexDateCell>
    )
  }

  // Return label (horizontal)
  if (isReturnLabel(i)) {
    return (
      <FlexDateCell {...cellProps} data-testid='return-label' isReturnLabel key={i}>
        <StyledText fontSize={1}>
          Return <ArrowRight size={10} />
        </StyledText>
      </FlexDateCell>
    )
  }

  // Depart label (vertical)
  if (isDepartLabel(i)) {
    return (
      <Label bg={'background.lightest'} m={0} color={'text.dark'} data-testid='depart-label' key={i}>
        <StyledText fontSize={1}>
          {' '}
          Depart <ArrowDown size={10} />
        </StyledText>
      </Label>
    )
  }

  // Selectable green, yellow, and red cells
  return (
    <ActiveFlexDateCell
      cellProps={isSelected(i) ? selectedCellProps : cellProps}
      travelDate={travelDate}
      returnDate={returnDate}
      priceThreshold={priceThreshold}
      minimumRetailPrice={minimumRetailPrice}
      price={Math.ceil(day?.fare)}
      position={i}
      url={url}
      day={day}
      isSelected={isSelected(i)}
      key={i}
      data-testid='flex-date-cell'
    />
  )
}

/**
 * --------------------------------------------------
 * ------ Generate Price Threshold Function ---------
 *
 * Price threshold - i.e. low, middle, high
 * of the legend component
 *
 *
 *
 * @param {dates} - qualified flex dates array []
 * @param {minimumRetailPrice} - selected min retail price
 * @returns {low, middle, high}
 * --------------------------------------------------
 */

export const generatePriceThreshold = ({ dates = [], travelDate, returnDate, minimumRetailPrice }) => {
  // Currently it's possible flex dates call will return same day searches that's cheaper
  // Search dates for the first non-sameDay date as the cheapest option
  const minPriceDate = dates?.find((date) => {
    const [dateTravelDate, dateReturnDate] = [date?.departDate, date?.returnDate]
    return dateTravelDate !== travelDate || dateReturnDate !== returnDate
  })

  const [minFlexPrice, maxFlexPrice] = [
    Math.ceil(minPriceDate?.fare),
    Math.ceil(dates[dates?.length - 1]?.fare),
  ]

  const roundedRetailPrice = Math.ceil(minimumRetailPrice)
  return {
    low: minFlexPrice,
    middle: roundedRetailPrice,
    high: maxFlexPrice,
  }
}

/**
 * --------------------------------------------------
 * ---------- Generate Price Map Function -----------
 *
 * We generate a flattened array of price map
 * corresponding to tile index i
 *
 * For example: i=== 44 is center
 * If say 2022-08-04 is start, 2022-08-07 is end
 * end date +/- 1 day === to index +/- 1
 * start date +/- 1 day === to index +/- 8
 *
 * Apply above logic to convert dates into position
 *
 *
 * @param {dates} - qualified flex dates array []
 * @param {minimumRetailPrice} - selected min retail price
 * @returns {low, middle, high}
 * --------------------------------------------------
 */

/* istanbul ignore next */
export const generatePriceMap = ({ dates, travelDate, returnDate, minimumRetailPrice }) => {
  let priceMap = [...Array(72)]

  priceMap[CENTER_INDEX] = { fare: minimumRetailPrice }

  dates?.forEach((date) => {
    let startDayDiff = moment(date.departDate).diff(moment(travelDate), 'days') * 8
    let endDayDiff = moment(date.returnDate).diff(moment(returnDate), 'days')

    if (startDayDiff + endDayDiff !== 0) {
      priceMap[CENTER_INDEX + startDayDiff + endDayDiff] = date
    }
  })
  return priceMap
}
